import classNames from 'classnames';
import { navigate } from 'gatsby';
import React, { use } from 'react';

import AnimatedExpandWrapper from 'src/components/common/animated-expand-wrapper';
import RedesignDownChevron from 'src/components/icons/redesign-down-chevron';
import { SiteContext } from 'src/context/site-context';
import { useToggle } from 'src/custom-hooks/use-toggle';
import type { BuildType } from 'src/enums/build/build-type';
import {
  getAllLanguagesForBuildType,
  shouldIncludeLanguageInUrlForBuildType,
} from 'src/enums/build/build-type';
import type { Language } from 'src/enums/language';
import {
  getDisplayTextForLanguage,
  getFlagIconForLanguage,
  getUrlValueForLanguage,
} from 'src/enums/language';
import { preparePath } from 'src/helpers/navigation-helper';
import { trimPath } from 'src/helpers/path-matching-helper';
import { updateUrlPreservingQueryParams } from 'src/helpers/query-string-helper';

import * as styles from './language-selection.module.scss';

const navigateToPathForLanguage = (language: Language, path: string, buildType: BuildType) => {
  const trimmedPath = trimPath(path);
  const pathWithLanguage = shouldIncludeLanguageInUrlForBuildType(buildType)
    ? `/${getUrlValueForLanguage(language)}/${trimmedPath}/`
    : `/${trimmedPath}/`;

  navigate(updateUrlPreservingQueryParams(preparePath(pathWithLanguage)));
};

type LanguageSelectionProps = {
  text?: string;
};

const LanguageSelection = ({ text }: LanguageSelectionProps) => {
  const { styling, pageLanguages, language: currentLanguage, buildType } = use(SiteContext);
  const [menuOpen, setMenuOpen, hideMenu] = useToggle(false);

  const allLanguagesForBuildType = getAllLanguagesForBuildType(buildType);
  const orderedLanguages = allLanguagesForBuildType
    .map((l) => pageLanguages?.find((p) => p.language === l))
    .filter((p) => !!p);

  if (orderedLanguages.length <= 1) {
    return null;
  }

  const languagesToSelect = orderedLanguages.filter((p) => p.language !== currentLanguage);

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={classNames(styles.button, styles.menuButton)}
        onClick={() => setMenuOpen(!Boolean(menuOpen))}
      >
        <div className={styles.iconWrapper}>
          {getFlagIconForLanguage(currentLanguage, { size: '1.25rem' })}
        </div>
        <div className={styles.text}>{getDisplayTextForLanguage(currentLanguage)}</div>
        <div className={classNames(styles.chevron, Boolean(menuOpen) ? styles.chevronOpen : '')}>
          <RedesignDownChevron size="100%" fill={styling.colors.capeCod} />
        </div>
      </button>
      <AnimatedExpandWrapper className={styles.expandContainer} isExpanded={Boolean(menuOpen)}>
        <div className={styles.menu}>
          <div className={classNames(styles.text, styles.selectionText)}>{text}</div>
          <div className={styles.selectionWrapper}>
            {languagesToSelect.map(({ language, path }) => (
              <button
                key={language}
                type="button"
                className={classNames(styles.button, styles.selectionButton)}
                onClick={() => {
                  hideMenu();
                  navigateToPathForLanguage(language, path, buildType);
                }}
              >
                <div className={styles.iconWrapper}>
                  {getFlagIconForLanguage(language, { size: '1.25rem' })}
                </div>
                <div className={styles.text}>{getDisplayTextForLanguage(language)}</div>
              </button>
            ))}
          </div>
        </div>
      </AnimatedExpandWrapper>
    </div>
  );
};

export default LanguageSelection;
